import {
  ChatLeftQuote,
  CollectionPlay,
  Dot,
  Eye,
  EyeSlash,
  PersonVideo3,
  Stack,
  ThreeDotsVertical,
  Trash,
  UiRadios
} from 'react-bootstrap-icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Avatar, Dropdown, MenuProps, Space, Tooltip, Typography, message } from 'antd';

import { getCdnUrl } from '@/helpers';
import { formatDate } from '@/helpers/formatDate';
import { IUserNotification, enNotificationOrigin } from '@/interfaces/notification';
import { setNotification } from '@/redux/notification/slice';
import { useDeleteNotificationMutation, useToggleNotificationMutation } from '@/services/notifications';
import styled, { css, StyledProp } from '@/styled';

const Item = (props: IUserNotification & StyledProp) => {
  const navigate = useNavigate();
  const [deleteNotification] = useDeleteNotificationMutation();
  const [toggleNotification] = useToggleNotificationMutation();
  const dispatch = useDispatch();

  const handledeleteNotification = () => {
    deleteNotification(props.id)
      .then(() => {
        message.success('Notificação excluída com sucesso!');
        dispatch(setNotification(null));
      })
      .catch(() => {
        message.error('Erro ao excluir notificação');
      });
  };

  const handleToggleNotification = () => {
    toggleNotification({ id: props.id, read: !props.read })
      .then(() => {
        message.success('Ação realizada com sucesso!');
      })
      .catch(() => {
        message.error('Erro ao realizar ação!');
      });
  };

  const dropdownItems: MenuProps['items'] = [
    {
      icon: <Trash />,
      key: 0,
      label: <Typography.Link onClick={handledeleteNotification}>Excluir</Typography.Link>
    },
    {
      icon: props.read ? <EyeSlash /> : <Eye />,
      key: 1,
      label: (
        <Typography.Link onClick={handleToggleNotification}>
          {props.read ? 'Marcar como não lida' : 'Marcar como lida'}
        </Typography.Link>
      )
    }
  ];

  const goToNotificationTarget = () => {
    const routePathName = 'central-notificacoes';
    const {
      producer_name,
      producer_avatar,
      link,
      course_title,
      new_cover_url,
      cover_url,
      description,
      payload,
      message,
      title,
      course_hash
    } = props;

    const course = {
      producer: { name: producer_name, avatar: producer_avatar },
      courseLink: link,
      title: course_title,
      cover: new_cover_url ?? cover_url,
      courseHash: course_hash
    };

    const notification = {
      origin: enNotificationOrigin.update,
      course,
      description,
      type: payload.type,
      message,
      title
    };

    if (props.read === false) {
      toggleNotification({ id: props.id, read: true });
    }

    dispatch(setNotification(notification));

    if (!window.location.pathname.includes(routePathName) && payload.type === 'news') {
      navigate(routePathName);
    }

    if (!window.location.pathname.includes(routePathName)) {
      navigate(`/curso/${link?.split('curso/')[1]}`);
    }
  };

  const renderIcon: { [key: string]: any } = {
    comment: <ChatLeftQuote size={24} className='icon' />,
    module: <Stack size={24} className='icon' />,
    lesson: <PersonVideo3 size={24} className='icon' />,
    content: <UiRadios size={24} className='icon' />,
    newcoursetrack: <CollectionPlay size={24} className='icon' />,
    news: <Avatar src={getCdnUrl(props.new_cover_url, '')} size={36} className='icon' />
  };

  return (
    <div className={props.className}>
      {renderIcon[props.payload.type]}
      <Space size={0} rootClassName='content' direction='vertical' onClick={goToNotificationTarget}>
        <Typography.Text ellipsis>{props?.description}</Typography.Text>

        <Tooltip title={props?.description} className='tooltip' arrow={false}>
          <Typography.Text ellipsis className='courseTitle'>
            {props?.course_title}
          </Typography.Text>
        </Tooltip>
      </Space>
      <Dot className='dot' size={24} data-read={props.read} />
      <div className='sideContent'>
        <Typography.Text>{formatDate(props.created_at)}</Typography.Text>
        <Dropdown menu={{ items: dropdownItems }} trigger={['click']} className='dropdown'>
          <ThreeDotsVertical size={24} />
        </Dropdown>
      </div>
    </div>
  );
};

export default styled(Item)(({ theme }) => {
  return css`
    display: grid;
    align-self: center;
    justify-content: space-between;
    width: 100%;
    padding: ${theme.space.spacing(3, 6)};
    grid-gap: ${theme.space.pxToRem(8)};
    grid-template-columns: 36px 300px auto 60px;
    border-bottom: 1px solid ${theme.variables.borderColor};

    .dot {
      color: ${theme.colorPrimary};

      &[data-read='true'] {
        color: transparent;
      }
    }

    .icon {
      align-self: center;
    }

    ${theme.breakpoints.down('md')} {
      grid-template-columns: 78% 4% 20%;

      .icon {
        display: none;
      }
    }

    .ant-typography {
      height: 24px;
    }

    .courseTitle {
      color: ${theme.colorTextTertiary};
    }

    .sideContent {
      display: grid;
      justify-items: flex-end;
      width: 60px;

      > span {
        font-size: 10px;
      }

      .dropdown {
        cursor: pointer;
      }
    }

    .content {
      .ant-typography {
        cursor: pointer;
      }

      ${theme.breakpoints.down('lg')} {
        .tooltip {
          display: none;
        }
      }
    }
  `;
});
